import { ClockCircleOutlined, CloseOutlined } from "@ant-design/icons";
import { Button, Loading, Popup } from "antd-mobile";
import style from "css/hotdesking.module.css";
import React, { useCallback, useLayoutEffect, useState } from "react";
import SearchTime from "./SearchTime";
import dayjs from "dayjs";
import HotdeskingConfig from "../Config";
import axios from "axios";
import { useTranslation } from "react-i18next";

function BookExtend({ isOpen, book, onClose, onRefreshBook, onError }) {
  const { t, i18n } = useTranslation();
  const [init, setInit] = useState(true);
  const [isActive, setIsActive] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpenTime, setIsOpenTime] = useState(false);
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [duration, setDuration] = useState(0);
  const [availableExtendTime, setAvailableExtendTime] = useState(0);

  const getDefaultTime = useCallback(() => {
    setDate(dayjs(new Date(book.endDttm)).format("YYYY-MM-DD"));
    setTime(dayjs(new Date(book.endDttm)).format("HH:mm"));
  }, [book]);

  const getAvailableExtendTime = useCallback(() => {
    const config = {
      params: {
        resource_id: book.resource.id,
        max_dttm:
          dayjs(new Date(book.endDttm)).format("YYYY-MM-DD") + " 23:59:59",
        end_dttm: dayjs(new Date(book.endDttm)).format("YYYY-MM-DD HH:mm:ss"),
      },
    };
    axios.get(`${HotdeskingConfig.baseURI}/book/extend`, config).then((res) => {
      setAvailableExtendTime(res.data - 1);
    });
  }, [book]);

  useLayoutEffect(() => {
    if (isOpen && init) {
      getDefaultTime();
      getAvailableExtendTime();
      setInit(false);
    }
  }, [getAvailableExtendTime, getDefaultTime, init, isOpen]);

  const onExtend = () => {
    setIsLoading(true);
    const data = {
      book_id: book.id,
      resource_id: book.resource.id,
      end_dttm: date + " " + time,
      // end_dttm: date + " " + time.format("YYYY-MM-DD HH:mm"),
      reference_dttm: dayjs(new Date(book.endDttm)).format("YYYY-MM-DD HH:mm"),
      duration: duration,
    };
    axios
      .put(`${HotdeskingConfig.baseURI}/book/extend`, data)
      .then(() => {
        onRefreshBook();
        onClosePopup();
      })
      .catch((err) => {
        onClosePopup();
        if (err === undefined || err.response === undefined) {
          onError(t("HOTDESKING.MODAL.EXTEND.MESSAGE.ERROR"));
        } else {
          onError(err.response.data);
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const onClosePopup = () => {
    onClose();
    setInit(true);
  };

  const onOpenTime = () => {
    setIsOpenTime(true);
  };
  const onCloseTime = () => {
    setIsOpenTime(false);
  };

  const getMinutesDiff = (startDttm, endDttm) => {
    const format = "HH:mm";
    const t1 = dayjs(startDttm, format);
    const t2 = dayjs(endDttm, format);
    return t2.diff(t1, "minute");
  };

  const addMinutes = (dateTime, minutes) => {
    const format = "HH:mm";
    return dayjs(dateTime, "YYYY-MM-DD HH:mm")
      .add(minutes, "minute")
      .format(format);
  };

  const onTimeSelect = (time) => {
    const endTime = dayjs(new Date(book.endDttm)).format("HH:mm");
    const selectTime = dayjs(time, "HH:mm").format("HH:mm");

    if (selectTime < endTime) {
      setTime(dayjs(new Date(book.endDttm)).format("HH:mm"));
      setIsActive(false);
      return;
    }

    const duration = getMinutesDiff(endTime, selectTime);
    const startTime = dayjs(new Date(book.startDttm)).format("HH:mm");

    if (duration > availableExtendTime) {
      const maxEndTime = addMinutes(book.endDttm, availableExtendTime);
      setDuration(getMinutesDiff(startTime, maxEndTime));
      setTime(dayjs(maxEndTime, "HH:mm").format("HH:mm"));
    } else {
      setDuration(getMinutesDiff(startTime, selectTime));
      setTime(selectTime);
    }
    setIsActive(true);
    onCloseTime();
  };

  const formatDuration = () => {
    let hours = Math.floor(availableExtendTime / 60);
    let mins = availableExtendTime % 60;
    if (i18n.language === "ko-KR") {
      return `${String(hours).padStart(2, "0")}시간 ${String(mins).padStart(
        2,
        "0"
      )}분`;
    } else {
      return `${String(hours).padStart(2, "0")}:${String(mins).padStart(
        2,
        "0"
      )}`;
    }
  };

  return (
    <>
      <SearchTime
        isOpen={isOpenTime}
        onClose={onCloseTime}
        onSelect={onTimeSelect}
        time={time}
        type={"end"}
      />
      <Popup
        className={
          !isOpenTime
            ? `${style.popup} ${style.extend}`
            : `${style.popup} ${style.extend} ${style.reduce}`
        }
        visible={isOpen && !isOpenTime}
        onMaskClick={onClose}
      >
        <section className={style.extend_section}>
          <section className={style.extend_header}>
            <article className={style.extend_title}>
              <p>{t("HOTDESKING.MODAL.EXTEND.TITLE")}</p>
              <CloseOutlined
                className={style.title_icon}
                onClick={onClosePopup}
              />
            </article>
            <article className={style.extend_available_time}>
              <p>{t("HOTDESKING.MODAL.EXTEND.AVAILABLE_EXTEND_TIME")}</p>
              <span className={style.divider} />
              <p>{formatDuration()}</p>
            </article>
          </section>
          <section className={style.extend_body}>
            {isLoading ? (
              <Loading className={style.Loading_content} />
            ) : (
              <>
                <p className={style.body_title}>
                  {t("HOTDESKING.MODAL.EXTEND.EXPIRATION_TIME")}
                </p>
                <div className={style.body_content}>
                  <div className={style.date_content}>{date}</div>
                  <div className={style.time_content} onClick={onOpenTime}>
                    <p>{time}</p>
                    <ClockCircleOutlined className={style.time_icon} />
                  </div>
                </div>
              </>
            )}
          </section>
          <section className={style.extend_footer}>
            <Button className={style.cancel_button} onClick={onClosePopup}>
              {t("HOTDESKING.BUTTON.CANCEL")}
            </Button>
            <Button
              className={
                isActive
                  ? style.submit_button
                  : `${style.submit_button} ${style.disabled}`
              }
              disabled={!isActive}
              onClick={onExtend}
            >
              {t("HOTDESKING.BUTTON.CONFIRM")}
            </Button>
          </section>
        </section>
      </Popup>
    </>
  );
}

export default BookExtend;
